import React from "react";
import styled, { css } from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import { PortalCard } from "../../portal/PortalCard";
import { unmodule } from "../../util";
import { AffiliatorMyInfoContext } from "../../providers/ProvideAffiliatorMyInfo";
import useForm, { FAILED, SUBMITTING, SUCCEED } from "../../hooks/useForm";
import Alert from "../../components/Alert";

const MyInfoForm = (props) => {
  const { myInfo, setMyInfo } = React.use(AffiliatorMyInfoContext);
  const [showMyInfo, setShowMyInfo] = React.useState(false);
  const toggleShowMyInfo = React.useCallback(() => {
    setShowMyInfo((x) => !x);
  }, []);

  const canInput = React.useMemo(
    () => showMyInfo || !myInfo.referral_code,
    [showMyInfo, myInfo],
  );
  const showMyInfoInputProps = React.useMemo(
    () =>
      canInput
        ? {
            type: "text",
            disabled: false,
          }
        : {
            type: "password",
            disabled: true,
          },
    [canInput],
  );

  const initialState = React.useCallback(
    () => ({
      bank_name: myInfo.bank_name || "",
      bank_account_number: myInfo.bank_account_number || "",
      bank_account_holder: myInfo.bank_account_holder || "",
    }),
    [myInfo],
  );
  const { inputParams, handleSubmit, submitState } = useForm(
    {
      initialState: initialState,
      url: "/api/v1/users/affiliators/info",
      handleSuccess: ({ parsedResponse, statusCode }) => {
        if (statusCode === 200) {
          setTimeout(() => setShowMyInfo(false), 2000);
          setMyInfo(parsedResponse.data);
        }
      },
    },
    [myInfo],
  );

  return (
    <form onSubmit={handleSubmit}>
      <MyInfoTitleAction onClick={toggleShowMyInfo}>
        My Info
        <EyeIcon
          src={
            canInput
              ? unmodule(require("../../images/icons/eye.svg"))
              : unmodule(require("../../images/icons/eye-slash.svg"))
          }
        />
      </MyInfoTitleAction>
      {!myInfo.referral_code && (
        <Alert $info>
          Lengkapi data dibawah untuk bergabung dengan affiliator
        </Alert>
      )}
      <BankInfoFlex>
        <BankInfoFieldItem>
          <BankInfoFieldName>Bank</BankInfoFieldName>
          <BankInfoFieldInput
            required
            {...inputParams("bank_name")}
            {...showMyInfoInputProps}
          />
        </BankInfoFieldItem>
        <BankInfoFieldItem>
          <BankInfoFieldName>No. Rekening</BankInfoFieldName>
          <BankInfoFieldInput
            required
            {...inputParams("bank_account_number")}
            {...showMyInfoInputProps}
          />
        </BankInfoFieldItem>
      </BankInfoFlex>
      <BankInfoFieldItem>
        <BankInfoFieldName>Nama Pemilik</BankInfoFieldName>
        <BankInfoFieldInput
          required
          {...inputParams("bank_account_holder")}
          {...showMyInfoInputProps}
        />
      </BankInfoFieldItem>
      {canInput && (
        <MyInfoFormSubmitDiv>
          {submitState === SUBMITTING ? (
            <SubmitButton disabled type="button" $submitting>
              Loading...
            </SubmitButton>
          ) : submitState === SUCCEED ? (
            <SubmitButton disabled type="button" $succeed>
              Berhasil!
            </SubmitButton>
          ) : submitState === FAILED ? (
            <SubmitButton disabled type="button" $failed>
              Gagal
            </SubmitButton>
          ) : (
            <SubmitButton type="submit">Simpan</SubmitButton>
          )}
        </MyInfoFormSubmitDiv>
      )}
    </form>
  );
};

const CAN_SHARE = false;

const UnnamedComponent = (props) => {
  const { myInfo } = React.use(AffiliatorMyInfoContext);
  const [copied, setCopied] = React.useState(false);
  const copiedTimeoutIdRef = React.useRef();
  const copyReferralCode = React.useCallback(() => {
    if (copiedTimeoutIdRef.current) {
      clearTimeout(copiedTimeoutIdRef.current);
    }
    setCopied(true);
    copiedTimeoutIdRef.current = setTimeout(() => setCopied(false), 1000);
    (async () => {
      await navigator.clipboard.writeText(myInfo.referral_code);
    })();
  }, [myInfo]);

  return (
    <MyAffInfo>
      <MyInfoForm />
      <InnerSeparator />
      <div>
        <CardTitle>Kode Referral</CardTitle>
        <RefCodeInner onClick={copyReferralCode}>
          <CodeNumber>{myInfo.referral_code}</CodeNumber>
          {myInfo.referral_code ? (
            <CopyCodeDiv $copied={copied}>
              <img src={unmodule(require("../../images/icons/copy.svg"))} />
              {copied ? <span>copied!</span> : <span>copy</span>}
            </CopyCodeDiv>
          ) : (
            <React.Fragment>
              <div>
                lengkapi <b>My Info</b> terlebih dahulu
              </div>
              <div></div>
            </React.Fragment>
          )}
        </RefCodeInner>
        {CAN_SHARE && (
          <RefCodeShare>
            <img src={unmodule(require("../../images/icons/share.svg"))} />
            Share
          </RefCodeShare>
        )}
      </div>
      <InnerSeparator />
      <div>
        <CardTitle>Saldo</CardTitle>
        <CardMetric>{myInfo.balance || "-"}</CardMetric>
      </div>
    </MyAffInfo>
  );
};

export default UnnamedComponent;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 12px;
`;

const MyAffInfo = styled(PortalCard)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CardMetric = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: ${brand_orange_deep};
  font-variant-numeric: tabular-nums;
`;

const InnerSeparator = styled.div`
  background-color: #e5e7eb;
  height: 1px;
  margin-left: -24px;
  width: calc(100% + 48px);

  @media ${narrowScreenMediaQuery} {
    margin-left: -18px;
    width: calc(100% + 36px);
  }
`;

const RefCodeInner = styled.div`
  align-items: center;
  background: #fff7ed;
  border-radius: 10px;
  border: 1px solid rgba(216, 89, 7, 0.22);
  display: flex;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  transition: border-color 150ms ease;

  &:hover {
    border-color: ${brand_orange_deep};
  }
`;

const CodeNumber = styled.div`
  letter-spacing: 4px;
  font-weight: 800;
  color: ${brand_orange_deep};
  font-size: 15px;
`;

const CopyCodeDiv = styled.div`
  align-items: center;
  color: ${brand_orange_deep};
  display: flex;
  font-size: 13px;

  & > span {
    font-weight: 700;
    margin-left: 6px;
  }

  ${(props) =>
    props.$copied &&
    css`
      & > span {
        font-weight: 600;
        color: #16a34a;
      }
    `};

  & > img {
    width: 16px;
    height: 16px;
  }
`;

const RefCodeShare = styled.button`
  align-items: center;
  background: ${brand_gradient};
  border-radius: 999px;
  border: none;
  color: #ffffff;
  display: flex;
  font-weight: 800;
  font-size: 13px;
  gap: 6px;
  justify-content: center;
  margin-top: 8px;
  padding: 10px 0;
  width: 100%;
  cursor: pointer;
  transition: filter 150ms ease, transform 100ms ease;

  &:hover {
    filter: brightness(0.97);
    transform: translateY(-1px);
  }

  & > img {
    width: 16px;
    height: 16px;
  }
`;

const BankInfoFlex = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
`;

const BankInfoFieldItem = styled.div`
  position: relative;

  ${BankInfoFlex} > &:first-child {
    flex: 1 1 0;
  }

  ${BankInfoFlex} > &:last-child {
    flex: 2 1 0;
  }
`;

const BankInfoFieldName = styled.div`
  background-color: #ffffff;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  left: 8px;
  padding: 0 4px;
  position: absolute;
  top: -8px;
`;

const BankInfoFieldInput = styled.input`
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  color: #1f2937;
  font-size: 14px;
  padding: 10px 12px;
  width: calc(100% - 28px);
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &:focus {
    outline: none;
    border-color: ${brand_orange_deep};
    box-shadow: 0 0 0 3px rgba(216, 89, 7, 0.12);
  }

  &:disabled {
    background-color: #f9fafb;
    color: #6b7280;
  }
`;

const MyInfoTitleAction = styled(CardTitle)`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  margin-bottom: 16px;
  color: ${brand_orange_deep};
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
`;

const EyeIcon = styled.img`
  width: 18px;
  margin-left: 6px;
`;

const MyInfoFormSubmitDiv = styled.div`
  margin-top: 14px;
  text-align: right;
`;

const SubmitButton = styled.button`
  background: ${brand_gradient};
  border-radius: 999px;
  border: none;
  color: #ffffff;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.04em;
  padding: 10px 24px;
  cursor: pointer;
  transition: filter 150ms ease, transform 100ms ease;

  &:hover {
    filter: brightness(0.97);
    transform: translateY(-1px);
  }

  ${(props) =>
    props.$succeed &&
    css`
      background: #16a34a;
      cursor: not-allowed;
      &:hover {
        filter: none;
        transform: none;
      }
    `};

  ${(props) =>
    props.$failed &&
    css`
      background: #dc2626;
      cursor: not-allowed;
      &:hover {
        filter: none;
        transform: none;
      }
    `};

  ${(props) =>
    props.$submitting &&
    css`
      background: #9ca3af;
      cursor: not-allowed;
      &:hover {
        filter: none;
        transform: none;
      }
    `};
`;
